/* ===========================================================================
   ROVE NEXT — VIEW IN 360, INLINE ON THE PDP. 25 September 2026.

   "View in 360" used to be a link to /pages/ar, leaving the product page
   entirely. This opens an overlay that covers the main gallery slide instead
   — .bb-main already carries the aspect ratio, the rounded corners and the
   position:relative this needs, so the overlay only has to match its size.

   VIEWER ONLY. No options/configurator panel, no QR "view in my room", no
   save-scene — this is a quick look, not a rebuild of the full AR page.

   IN AN IFRAME, 26 September 2026. One Shopify product can need more than
   one Expivi model: Reya's 2 Piece and 4 Piece are separate catalogues, so
   changing Size has to swap the whole model. Expivi cannot do that in place —
   window.expivi._destroy() permanently closes its own event channels and
   nothing re-creates them — so each model gets its own <iframe>, and a swap
   is a fresh frame. The frame is srcdoc, so same-origin: this script still
   calls the model's window.expivi directly. It also keeps the global CSS of
   Expivi's options app (Vuetify) out of the product page.

   LAZY. Nothing loads until the shopper clicks. Closing hides the overlay
   rather than tearing the frame down, so reopening is instant.
   =========================================================================== */
(function () {
  var VIEWER_SRC = 'https://assets.expivi.net/viewer/latest/viewer.js';
  /* window.ExpiviComponent comes from the OPTIONS app, not viewer.js — both
     are needed, in this order, exactly as sections/rn-ar.liquid loads them. */
  var OPTIONS_SRC = 'https://assets.expivi.net/options/1.36.0/js/app.js';
  /* Saved scenes live on the Rove API — the same address and the same
     POST /api/scenes the full AR page uses (sections/rn-ar.liquid, whose
     api_base setting defaults to this), so a scene saved here shows up on
     /pages/my-saved-ar-scenes with nothing else changed. */
  var SCENES_API = 'https://app.roveconcepts.com/api/scenes';
  var MY_SCENES_URL = '/pages/my-saved-ar-scenes';
  /* Long enough for a slow phone to render the screenshot and upload it;
     short enough that nobody is left watching "Saving…" wondering. */
  var SAVE_TIMEOUT_MS = 30000;

  var productVariants = null;   /* promise of {variantId: [option values]} */
  function variantOptions() {
    if (productVariants) return productVariants;
    var handle = (location.pathname.match(/\/products\/([^\/?#]+)/) || [])[1];
    productVariants = fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        var map = {};
        ((p && p.variants) || []).forEach(function (v) { map[String(v.id)] = v.options || []; });
        return map;
      });
    productVariants.catch(function () { productVariants = null; });
    return productVariants;
  }

  /* The document inside the frame: just the viewer, full size. The options
     container is required by ExpiviComponent but never shown — and it is
     hidden by a WRAPPER, not by its own id: the options app is a Vue app
     mounted with `el`, which REPLACES the element it mounts on, so a rule on
     #o itself stopped matching the moment it rendered and its FABRIC / SIZE
     labels showed through over the model. */
  function frameDoc(catalogueId) {
    var cfg = JSON.stringify({
      catalogueId: Number(catalogueId),
      viewerContainer: '#v',
      optionContainer: '#o',
      token: window.RN_AR_TOKEN || '',
      /* Expivi's own Measurement button in its toolbar, beside AR and
         fullscreen. The component passes this whole object to the viewer as
         its options; the toolbar item reads showMeasurement. */
      showMeasurement: true
    }).replace(/</g, '\\u003c');
    return '<!doctype html><html><head><meta charset="utf-8">' +
      '<style>html,body{margin:0;height:100%;background:#FFFFFF;overflow:hidden}#v{position:absolute;inset:0}#ow{display:none!important}</style>' +
      '</head><body><div id="v"></div><div id="ow" hidden><div id="o"></div></div>' +
      '<script src="' + VIEWER_SRC + '"><\/script>' +
      '<script src="' + OPTIONS_SRC + '"><\/script>' +
      '<script>try{new ExpiviComponent.default(' + cfg + ')}catch(e){window.__rnArError=String(e&&e.message||e)}<\/script>' +
      '</body></html>';
  }

  /* THE MODEL FOLLOWS THE CHOSEN VARIANT. No mapping table: the model's own
     attributes are read back from Expivi once it has loaded, and each Shopify
     option value of the selected variant is matched against them.

     FABRIC. A material-group attribute whose materials are named exactly as
     the Colourway values are (Diane, Reya: Pearl, Oat, Dune, ...). Matched on
     the exact name, case aside. Any material-group attribute with "fabric" in
     its name counts, EXCEPT a pillow one — FABRIC PILLOW is a contrast
     cushion with its own palette and Shopify has no option for it.

     EVERYTHING ELSE (question attributes). Names do not match exactly here —
     Reya's Chaise "Left Chaise" is Expivi's ORIENTATION "Curved Chaise Left"
     — so a value matches when every word of the Shopify value appears in it
     (or the other way round), and only when exactly ONE value of that
     attribute matches. The display toggles (DIMENSIONS, GRID, ADD-ON PILLOW:
     values like "Dimensions-ON") never match a Shopify option, so they are
     left as the model set them. */
  function words(s) {
    return String(s || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  }
  function wordsWithin(a, b) {
    return a.length > 0 && a.every(function (w) { return b.indexOf(w) !== -1; });
  }

  function matchValue(attr, optionValues) {
    for (var i = 0; i < optionValues.length; i++) {
      var ov = String(optionValues[i]);
      if (attr.type === 'matgroup') {
        var exact = attr.values.filter(function (v) { return v.toLowerCase() === ov.toLowerCase(); });
        if (exact.length) return exact[0];
      } else {
        var ow = words(ov);
        var hits = attr.values.filter(function (v) {
          var vw = words(v);
          return wordsWithin(ow, vw) || wordsWithin(vw, ow);
        });
        if (hits.length === 1) return hits[0];
      }
    }
    return null;
  }

  /* [{name, type, values:[names]}] for one loaded model */
  function modelAttrs(api) {
    return Promise.resolve(api.getProductAttributes()).then(function (attrs) {
      return Promise.all((attrs || []).map(function (a) {
        if (a.type === 'matgroup') {
          if (!/fabric/i.test(a.name) || /pillow/i.test(a.name)) return null;
          return Promise.resolve(api.getMatgroupOptions(a.id)).then(function (mats) {
            return { name: a.name, type: 'matgroup', values: (mats || []).map(function (m) { return m.material_name || m.name; }).filter(Boolean) };
          }, function () { return null; });
        }
        if (a.type === 'question') {
          return { name: a.name, type: 'question', values: (a.attribute_values || []).map(function (v) { return v.name; }).filter(Boolean) };
        }
        return null;
      }));
    }).then(function (list) { return list.filter(Boolean); });
  }

  Array.prototype.slice.call(document.querySelectorAll('[data-bb-ar-open]')).forEach(function (openBtn) {
    var main = openBtn.closest('.bb-main');
    if (!main) return;
    var bb = openBtn.closest('[id^="rn-bb-"]') || document;

    var overlay = main.querySelector('[data-bb-ar-overlay]');
    var closeBtn = main.querySelector('[data-bb-ar-close]');
    var loading = main.querySelector('[data-bb-ar-loading]');
    var viewport = main.querySelector('.bb-ar-viewport');
    if (!overlay || !viewport) return;

    var defaultCatalogue = openBtn.getAttribute('data-bb-ar-catalogue');
    var catalogueByVariant = {};
    (openBtn.getAttribute('data-bb-ar-variants') || '').split(';').forEach(function (pair) {
      var p = pair.split(':');
      if (p.length === 2 && p[0] && p[1]) catalogueByVariant[p[0].trim()] = p[1].trim();
    });

    /* Both variant controllers — rn-pdp-variant.js on the ordinary PDPs and
       the config grid in rn-buybox.liquid on Reya — write the chosen variant
       into the cart form's id field. That field is the one thing they share,
       so it is what this reads. */
    var idInput = bb.querySelector('[data-bb-form] input[name="id"]');
    function currentVariantId() { return idInput ? String(idInput.value || '') : ''; }
    function catalogueFor(id) { return catalogueByVariant[id] || defaultCatalogue; }

    /* the live model: its frame, which catalogue, and whether it is ready */
    var model = null;

    /* ---- save scene ------------------------------------------------------ */
    var saveBtn = overlay.querySelector('[data-bb-ar-save]');
    var saveMsg = overlay.querySelector('[data-bb-ar-savemsg]');
    var customerId = overlay.getAttribute('data-bb-ar-customer') || '';
    var customerEmail = overlay.getAttribute('data-bb-ar-email') || '';
    var productTitle = overlay.getAttribute('data-bb-ar-title') || document.title;

    /* text, then optional [label, href] links; built as nodes, never HTML */
    function say(text, links) {
      if (!saveMsg) return;
      saveMsg.textContent = text;
      (links || []).forEach(function (l, i) {
        saveMsg.appendChild(document.createTextNode(i ? ' · ' : ' '));
        var a = document.createElement('a');
        a.textContent = l[0];
        a.href = l[1];
        saveMsg.appendChild(a);
      });
      saveMsg.hidden = false;
    }

    function withReturn(url) {
      if (!url) return url;
      var back = location.pathname + location.search;
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'return_to=' + encodeURIComponent(back);
    }

    function saveScene() {
      if (saveMsg) saveMsg.hidden = true;
      if (!customerId) {
        say('Sign in to save this scene to your account.', [
          ['Sign in', withReturn(overlay.getAttribute('data-bb-ar-login'))],
          ['Create account', withReturn(overlay.getAttribute('data-bb-ar-register'))]
        ]);
        return;
      }
      var m = model;
      var api = m && m.ready && m.frame.contentWindow && m.frame.contentWindow.expivi;
      if (!api) return;
      saveBtn.disabled = true;
      saveBtn.textContent = 'Saving…';
      /* A time limit on the WHOLE save, not just the POST: the screenshot and
         saveConfiguration calls into the viewer can stall as well as the Rove
         API, and any of them left the button on "Saving…" for ever. The abort
         stops the POST if that is the part still running. */
      var ctrl = window.AbortController ? new AbortController() : null;
      var timer;
      var timedOut = new Promise(function (_, reject) {
        timer = setTimeout(function () {
          if (ctrl) ctrl.abort();
          reject(new Error('timeout'));
        }, SAVE_TIMEOUT_MS);
      });
      var saving = Promise.resolve(api.getCameraTransform())
        .then(function (t) {
          /* same size and camera mode as the full AR page, so the thumbnails
             on the saved-scenes page all match */
          return Promise.all([
            api.getScreenshot(529, 342, 'CUSTOM_CAMERA_POSITION', 'image/jpeg', t),
            api.saveConfiguration(0, 0)
          ]);
        })
        .then(function (res) {
          return fetch(SCENES_API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              shopify_customer_id: String(customerId),
              shopify_customer_email: customerEmail,
              title: productTitle + ' - Customization',
              configuration: res[1],
              image_base64: res[0]
            }),
            signal: ctrl ? ctrl.signal : undefined
          });
        });
      Promise.race([saving, timedOut])
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          say('Scene saved.', [['View my scenes', MY_SCENES_URL]]);
        })
        .catch(function (err) {
          console.error('[rn-pdp-ar] save scene', err);
          /* A timed-out upload may still have arrived, so point at the list
             before suggesting a retry that could save it twice. */
          if (err && (err.message === 'timeout' || err.name === 'AbortError')) {
            say('Saving is taking too long. Check your saved scenes before trying again.',
                [['View my scenes', MY_SCENES_URL]]);
          } else {
            say('The scene could not be saved. Please try again.');
          }
        })
        .then(function () {
          clearTimeout(timer);
          saveBtn.disabled = false;
          saveBtn.textContent = 'Save scene';
        });
    }

    if (saveBtn) saveBtn.addEventListener('click', saveScene);

    function showLoading(text) {
      if (!loading) return;
      loading.hidden = false;
      loading.textContent = text || 'Loading 3D view…';
    }

    function build(catalogueId) {
      if (model && model.frame) model.frame.remove();
      showLoading();
      /* nothing to save until the new model is on screen */
      if (saveBtn) saveBtn.hidden = true;
      if (saveMsg) saveMsg.hidden = true;
      var m = { catalogueId: catalogueId, ready: false, attrs: null, applied: {}, frame: document.createElement('iframe') };
      model = m;
      m.frame.title = '3D view';
      m.frame.setAttribute('allow', 'fullscreen; xr-spatial-tracking; camera; accelerometer; gyroscope');
      m.frame.setAttribute('allowfullscreen', '');
      m.frame.srcdoc = frameDoc(catalogueId);
      viewport.appendChild(m.frame);

      /* wait for the model inside the frame to say it has finished loading */
      var tries = 0;
      (function waitForExpivi() {
        if (model !== m) return; /* replaced by a newer frame meanwhile */
        var w = m.frame.contentWindow;
        var api = w && w.expivi;
        if (w && w.__rnArError) {
          console.error('[rn-pdp-ar]', w.__rnArError);
          showLoading('The 3D view could not be loaded.');
          return;
        }
        if (api && api._events && api._events.onChange) {
          api._events.onChange.subscribe(function (event) {
            if (event.name !== 'product_finished_loading' || m.ready || model !== m) return;
            m.ready = true;
            if (loading) loading.hidden = true;
            if (saveBtn) saveBtn.hidden = false;
            applyVariant();
          });
          return;
        }
        if (tries++ < 300) setTimeout(waitForExpivi, 100);
        else showLoading('The 3D view could not be loaded.');
      })();

      /* the loading text must never sit forever over a model that is there */
      setTimeout(function () { if (model === m && loading) loading.hidden = true; }, 15000);
    }

    function applyVariant() {
      var m = model;
      if (!m || !m.ready) return;
      var api = m.frame.contentWindow && m.frame.contentWindow.expivi;
      if (!api || typeof api.getProductAttributes !== 'function') return;
      var id = currentVariantId();
      if (!m.attrs) m.attrs = modelAttrs(api);
      Promise.all([m.attrs, variantOptions()]).then(function (res) {
        if (model !== m) return;
        var optionValues = res[1][id] || [];
        res[0].forEach(function (attr) {
          var value = matchValue(attr, optionValues);
          /* only on a real change: each set rebuilds the model */
          if (!value || m.applied[attr.name] === value) return;
          m.applied[attr.name] = value;
          Promise.resolve(api.setProductAttributeNamed(attr.name, value)).catch(function (err) {
            delete m.applied[attr.name];
            console.warn('[rn-pdp-ar] could not set', attr.name, 'to', value, err);
          });
        });
      }).catch(function (err) {
        m.attrs = null;
        console.warn('[rn-pdp-ar] could not read the model', err);
      });
    }

    /* after any selection in the buy box: the right model, then the right
       fabric and orientation on it. A closed viewer is brought up to date
       the next time it opens instead. */
    function sync() {
      if (overlay.hidden) return;
      var want = catalogueFor(currentVariantId());
      if (!model || model.catalogueId !== want) build(want);
      else applyVariant();
    }

    function open() {
      overlay.hidden = false;
      sync();
    }

    function close() {
      overlay.hidden = true;
    }

    openBtn.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);

    /* The controllers write the id during their own click handling; read it
       just after. */
    bb.addEventListener('click', function (e) {
      if (overlay.hidden || overlay.contains(e.target)) return;
      setTimeout(sync, 150);
    });

    /* Choosing a photo — a thumbnail, a dot, or an arrow — means the shopper
       wants the gallery back, so the 3D view steps aside. Hidden, not torn
       down: "View in 360" reopens it instantly. */
    var gallery = main.parentElement;
    if (gallery) gallery.addEventListener('click', function (e) {
      if (overlay.hidden) return;
      if (e.target.closest('.bb-th, .bb-dot, [data-bb-prev], [data-bb-next]')) close();
    });
  });
})();
